import React, { useMemo, useState } from 'react';
import { usePos } from '../context/PosContext';
import { printReportAuto } from '../services/receipt';
import './Entry.css';

interface Line {
  productId: string;
  name: string;
  qty: number;
  cost: number;
}

interface Pending {
  productId: string;
  name: string;
  qty: string;
  cost: string;
  price: string;
}

const REASONS = ['Damage', 'Expiry', 'Wastage', 'Self Use', 'Theft', 'Adjustment'];

// Matches goods_sold_new_layout.xml.
const StockOut: React.FC = () => {
  const { products, stockOut, movements, config } = usePos();
  const [lines, setLines] = useState<Line[]>([]);
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState<Pending | null>(null);
  const [reason, setReason] = useState(REASONS[0]);

  const lastSold = useMemo(() => {
    const out = movements.filter((m) => m.reason === 'stock_out');
    return out.length ? out[out.length - 1].quantity : 0;
  }, [movements]);

  const findProduct = (term: string) =>
    products.find((p) => p.barcode === term || String(p.id) === term || p.name.toLowerCase() === term.toLowerCase());

  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || !search.trim()) return;
    const p = findProduct(search.trim());
    if (p) {
      setPending({ productId: p.id, name: p.name, qty: '1', cost: String(p.costPrice ?? 0), price: String(p.price) });
      setSearch('');
    }
  };

  const pendingTotal = pending ? (parseFloat(pending.qty) || 0) * (parseFloat(pending.cost) || 0) : 0;

  const commit = () => {
    if (!pending) return;
    const qty = parseFloat(pending.qty) || 0;
    const cost = parseFloat(pending.cost) || 0;
    if (qty <= 0) return;
    setLines((prev) => {
      const f = prev.find((l) => l.productId === pending.productId);
      if (f) return prev.map((l) => (l.productId === pending.productId ? { ...l, qty: l.qty + qty } : l));
      return [...prev, { productId: pending.productId, name: pending.name, qty, cost }];
    });
    setPending(null);
  };

  const removeLine = (id: string) => setLines((prev) => prev.filter((l) => l.productId !== id));
  const total = lines.reduce((s, l) => s + l.qty * l.cost, 0);

  const stockOf = (id: string) => products.find((p) => p.id === id)?.stock ?? 0;

  const save = () => {
    if (!lines.length) return;
    stockOut(lines.map((l) => ({ productId: l.productId, quantity: l.qty })), reason);
    setLines([]);
    alert('Stock out saved.');
  };

  // Print the current stock-out draft as a thermal slip (ESC/POS), falling back
  // to the browser print dialog.
  const printSlip = () => {
    if (!lines.length) return;
    const rows: (string | number)[][] = lines.map((l) => [l.name, l.qty, l.cost.toFixed(2), (l.qty * l.cost).toFixed(2)]);
    rows.push(['', '', 'NET', total.toFixed(2)]);
    printReportAuto('Stock Out', `${config.storeName} · ${reason}`, ['Item', 'Qty', 'Cost', 'Total'], rows);
  };

  return (
    <div className="es-page">
      <div className="es-head">
        <span>STOCK OUT</span>
        <span className="es-sub">{config.storeName}</span>
      </div>

      <div className="es-toolbar">
        <div className="es-search" style={{ flex: 1 }}>
          <input placeholder="Search by Code/Barcode/Name" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={onSearchKey} />
          <div className="es-search-btn">🔍</div>
        </div>
        <select value={reason} onChange={(e) => setReason(e.target.value)} style={{ minWidth: 160, padding: 10 }}>
          {REASONS.map((r) => (
            <option key={r}>{r}</option>
          ))}
        </select>
      </div>

      <div className="es-entry">
        <input className="es-ebox" placeholder="Qty" value={pending?.qty ?? ''} onChange={(e) => pending && setPending({ ...pending, qty: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && commit()} />
        <input className="es-ebox" placeholder="Cost Price" value={pending?.cost ?? ''} onChange={(e) => pending && setPending({ ...pending, cost: e.target.value })} />
        <input className="es-ebox" placeholder="Price" value={pending?.price ?? ''} onChange={(e) => pending && setPending({ ...pending, price: e.target.value })} />
        <div className="es-ebox total" onClick={commit} title="Add to list">
          {pendingTotal.toFixed(2)}
        </div>
      </div>

      <div className="es-items">
        {lines.length === 0 ? (
          <div className="es-empty">{pending ? `Adding ${pending.name} — set Qty, press Enter` : 'Search a product to remove stock'}</div>
        ) : (
          lines.map((l) => (
            <div className="es-item" key={l.productId}>
              <span className="es-del" onClick={() => removeLine(l.productId)}>
                ✕
              </span>
              <span className="es-item-name">
                {l.name} <span className="es-sub" style={{ color: 'var(--color-grayish)' }}>(in stock {stockOf(l.productId)})</span>
              </span>
              <span className="es-orange">{l.qty}</span>
              <span className="es-x">X</span>
              <span className="es-orange">{l.cost.toFixed(2)}</span>
              <span className="es-x">=</span>
              <span className="es-item-total">{(l.qty * l.cost).toFixed(2)}</span>
            </div>
          ))
        )}
      </div>

      <div className="es-bottom">
        <div className="es-lastbill">Last Goods Sold : {lastSold.toFixed(2)}</div>
        <div className="es-netbar">NET AMT : {total.toFixed(2)}</div>
      </div>

      <div className="es-actions">
        <button className="es-btn-orange" onClick={save} disabled={!lines.length}>
          Save Stock Out
        </button>
        <button className="es-btn-grey" onClick={printSlip} disabled={!lines.length}>
          Print
        </button>
      </div>
    </div>
  );
};

export default StockOut;
